import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LinkOverlay } from '@sorare/core/src/atoms/navigation/Box';
import { LabelL, LabelM, LabelS } from '@sorare/core/src/atoms/typography';
import { ANY_SPORT_MY_CLUB_SLUG_HISTORY_FIXTURE } from '@sorare/core/src/constants/__generated__/routes';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useSportContext } from '@sorare/core/src/contexts/sport';

import { LobbyTile } from 'components/home/LobbyTile';

import bigCompetitionHistoryBg from './assets/bigCompetitionHistoryBg.jpg';
import competitionHistorySquare from './assets/competitionHistorySquare.png';

const Wrapper = styled.div`
  opacity: 1;
  position: relative;

  transition: opacity 0.05s ease-in-out;

  &:hover {
    opacity: 0.5;
  }
`;

const StyledLobbyTile = styled(LobbyTile)`
  display: flex;
  height: 205px;
  align-items: end;

  background: url(${bigCompetitionHistoryBg}) no-repeat center center;
  background-size: cover;
`;

const CTA = styled(Button)`
  margin-left: auto;
`;

const CompactVariant = () => {
  const { currentUser } = useCurrentUserContext();
  const { generateSportContextPath } = useSportContext();

  return (
    <Wrapper>
      <LinkOverlay
        as={Link}
        to={generateSportContextPath(ANY_SPORT_MY_CLUB_SLUG_HISTORY_FIXTURE, {
          params: {
            fixture: 'past',
            slug: currentUser?.slug || '',
          },
        })}
      >
        <Horizontal>
          <img width={48} height={48} src={competitionHistorySquare} alt="" />
          <Vertical>
            <LabelM bold>
              <FormattedMessage
                id="ForYou.CompetitionHistory.title"
                defaultMessage="Competition History"
              />
            </LabelM>
            <LabelM as="span" color="var(--c-neutral-600)">
              <FormattedMessage
                id="ForYou.CompetitionHistory.subtitle"
                defaultMessage="Check out your past performance"
              />
            </LabelM>
          </Vertical>
          <CTA size="small" color="tertiary">
            <FormattedMessage
              id="ForYou.CompetitionHistory.CTA"
              defaultMessage="View"
            />
          </CTA>
        </Horizontal>
      </LinkOverlay>
    </Wrapper>
  );
};

const DefaultVariant = () => {
  const { currentUser } = useCurrentUserContext();
  const { generateSportContextPath } = useSportContext();

  return (
    <StyledLobbyTile
      outlineColor="#CC9200"
      label={
        <LabelS bold color="var(--c-nl-100-opaque)">
          <FormattedMessage
            id="ForYou.CompetitionHistory.label"
            defaultMessage="Competition History"
          />
        </LabelS>
      }
    >
      <LinkOverlay
        as={Link}
        to={generateSportContextPath(ANY_SPORT_MY_CLUB_SLUG_HISTORY_FIXTURE, {
          params: {
            fixture: 'past',
            slug: currentUser?.slug || '',
          },
        })}
      >
        <LabelL bold>
          <FormattedMessage
            id="ForYou.CompetitionHistory.subtitle"
            defaultMessage="Check out your past performance"
          />
        </LabelL>
      </LinkOverlay>
    </StyledLobbyTile>
  );
};

type Props = {
  compact?: boolean;
};

export const CompetitionHistory = ({ compact }: Props) => {
  if (compact) {
    return <CompactVariant />;
  }

  return <DefaultVariant />;
};
